import { v4 as uuidv4 } from "uuid";

import { EditorNodeName } from "@/enums/customAttributes";
import { SERVICE_CUSTOM } from "@/helpers/constants";
import { BRACKET_COLORS } from "@/helpers/constants/attributeBuilder";
import { buildAttributeContext, stripColonPrefix } from "@/helpers/parser";
import { parserInstance } from "@/helpers/parser/parser";

import type { EditorNode } from "@/models/TextEditor";
import type { FormulaOption } from "@/models/options";
import type { IAttribute } from "@/models/orchestration";
import type {
  ArgumentListCst,
  AtomicExpressionCst,
  AttributeReferenceCst,
  BinaryExpressionCst,
  BracketPair,
  ExpressionCst,
  FunctionCallCst,
  FunctionCallMetadata,
  NumberLiteralCst,
  ParenthesizedExpressionCst,
  ParsedFormulaMetadata,
  StringLiteralCst,
  TypedCstNode,
  UppercaseIdentifierCst,
  VisitorContext,
} from "@/models/parser";
import type { CstNode, IToken } from "chevrotain";

const BaseVisitor = parserInstance.getBaseCstVisitorConstructor();

export type { BracketPair, FunctionCallMetadata, ParsedFormulaMetadata };

class FormulaVisitor extends BaseVisitor {
  private tokens: EditorNode[] = [];
  private context: VisitorContext;
  private metadata: ParsedFormulaMetadata = {
    bracketPairs: new Map(),
    functionCalls: [],
    totalTokens: 0,
  };

  constructor(
    functionsByNameMap: Record<string, FormulaOption>,
    serviceAttributes: IAttribute[]
  ) {
    super();
    this.validateVisitor();
    this.context = {
      functionsByNameMap,
      serviceAttributes,
      bracketDepth: 0,
      sequentialIndex: 0,
    };
  }

  getMetadata(): ParsedFormulaMetadata {
    return this.metadata;
  }

  private generateUIId(token: IToken): string {
    const index = this.context.sequentialIndex;
    const content = token.image;

    if (content.length < 30) {
      return `${index}:${content}`;
    }

    return `${index}:${this.fastHash(content)}`;
  }

  private fastHash(str: string): string {
    if (str.length < 10) {
      return str;
    }

    const first = str.charCodeAt(0);
    const middle = str.charCodeAt(Math.floor(str.length / 2));
    const last = str.charCodeAt(str.length - 1);

    return `${first.toString(36)}${middle.toString(36)}${last.toString(36)}${str.length.toString(36)}`;
  }

  private createEditorNode(
    token: IToken,
    type: EditorNodeName,
    additionalProps: Partial<EditorNode> = {}
  ): EditorNode {
    const id = uuidv4();
    this.context.sequentialIndex++;

    const node: EditorNode = {
      id,
      type,
      content: token.image,
      atomic: this.isAtomicToken(type),
      isOperand: this.isOperandType(type),
      isOperator:
        type === EditorNodeName.operator || type === EditorNodeName.function,
      error: {},
      ...additionalProps,
    };

    return node;
  }

  private isAtomicToken(type: EditorNodeName): boolean {
    return type === EditorNodeName.attribute;
  }

  private isOperandType(type: EditorNodeName): boolean {
    return [
      EditorNodeName.number,
      EditorNodeName.attribute,
      EditorNodeName.regex,
      EditorNodeName.unrecognized,
      EditorNodeName.function,
    ].includes(type);
  }

  private addToken(node: EditorNode): void {
    this.tokens.push(node);
  }

  private getCurrentIndex(): number {
    return this.tokens.length;
  }

  private getBracketColor(depth: number): string {
    return BRACKET_COLORS[depth % BRACKET_COLORS.length];
  }

  private createBracketPair(
    _openingToken: IToken,
    _closingToken: IToken,
    openingIndex: number,
    closingIndex: number
  ): void {
    const openingId = this.tokens[openingIndex].id ?? "";
    const closingId = this.tokens[closingIndex].id ?? "";
    const depth = this.context.bracketDepth;
    const color = this.getBracketColor(depth);

    const pair: BracketPair = {
      openingId,
      closingId,
      openingIndex,
      closingIndex,
      depth,
      color,
    };

    this.metadata.bracketPairs.set(openingId, pair);
    this.metadata.bracketPairs.set(closingId, pair);

    this.tokens[openingIndex].context = {
      matchedBracketsId: closingId,
      matchedBracketsIndex: closingIndex,
      color,
    };

    this.tokens[closingIndex].context = {
      matchedBracketsId: openingId,
      matchedBracketsIndex: openingIndex,
      color,
    };
  }

  private handleClosingBracket(
    closingToken: IToken | undefined,
    openingToken: IToken,
    openingNode: EditorNode,
    openingIndex: number
  ): number {
    if (!closingToken) {
      openingNode.context = {
        matchedBracketsId: undefined,
        color: "",
      };
      return openingIndex;
    }

    const closingNode = this.createEditorNode(
      closingToken,
      EditorNodeName.parenthesis
    );
    const closingIndex = this.getCurrentIndex();
    this.addToken(closingNode);

    this.createBracketPair(
      openingToken,
      closingToken,
      openingIndex,
      closingIndex
    );

    return closingIndex;
  }

  private isValidFunction(name: string): boolean {
    const upperName = name.toUpperCase();
    return upperName in this.context.functionsByNameMap;
  }

  private isValidAttribute(name: string): boolean {
    const cleanName = stripColonPrefix(name);
    const cleanNameLower = cleanName.toLowerCase();
    const exactMatch = this.context.serviceAttributes.find(
      (attr) =>
        attr.name === cleanName ||
        (attr.id === cleanName && cleanName !== SERVICE_CUSTOM)
    );
    if (!exactMatch) {
      return false;
    }
    const hasOtherPartialMatches = this.context.serviceAttributes.some(
      (attr) =>
        attr !== exactMatch &&
        (attr.name?.toLowerCase().startsWith(cleanNameLower) ||
          attr.id?.toLowerCase().startsWith(cleanNameLower))
    );
    return !hasOtherPartialMatches;
  }

  private addValidationError(node: EditorNode, errorMessage: string): void {
    if (!node.error?.message) {
      node.error = { message: errorMessage };
    }
  }

  private createValidatedOperatorNode(operatorToken: IToken): EditorNode {
    const node = this.createEditorNode(operatorToken, EditorNodeName.operator);

    if (!this.isValidFunction(operatorToken.image)) {
      this.addValidationError(node, `Unknown operator: ${operatorToken.image}`);
    }

    return node;
  }

  private findAttribute(name: string): IAttribute | undefined {
    const cleanName = stripColonPrefix(name);
    return this.context.serviceAttributes.find(
      (attr) =>
        attr.name === cleanName ||
        (attr.id === cleanName && cleanName !== SERVICE_CUSTOM)
    );
  }

  private addAttributeNodeFromFunctionCall(
    functionToken: IToken,
    attribute: IAttribute
  ): void {
    const attrNode = this.createEditorNode(
      functionToken,
      EditorNodeName.attribute,
      { attributeId: attribute.id }
    );
    attrNode.content = attribute.name;

    const isCustomWithContext =
      attribute.id === SERVICE_CUSTOM && attribute.context;
    const attributeContext =
      isCustomWithContext && buildAttributeContext(attribute);

    if (attributeContext) {
      attrNode.context = attributeContext;
    }

    this.addToken(attrNode);
  }

  expression(ctx: ExpressionCst): void {
    this.visit(ctx.logicalExpression);
  }

  logicalExpression(ctx: BinaryExpressionCst): void {
    this.visit(ctx.lhs);

    const LogicalAnd = ctx.LogicalAnd;
    const LogicalOr = ctx.LogicalOr;

    if (LogicalAnd || LogicalOr) {
      const operators = (LogicalAnd || LogicalOr) as IToken[];
      const rhsExpressions = ctx.rhs as CstNode[];

      for (let i = 0; i < operators.length; i++) {
        const operatorNode = this.createValidatedOperatorNode(operators[i]);
        this.addToken(operatorNode);
        this.visit(rhsExpressions[i]);
      }
    }
  }

  comparisonExpression(ctx: BinaryExpressionCst): void {
    this.visit(ctx.lhs);

    const operators = [
      ...(ctx.Equals || []),
      ...(ctx.NotEquals || []),
      ...(ctx.LessThan || []),
      ...(ctx.GreaterThan || []),
      ...(ctx.LessEquals || []),
      ...(ctx.GreaterEquals || []),
    ] as IToken[];

    if (operators.length) {
      const rhsExpressions = ctx.rhs as CstNode[];

      for (let i = 0; i < operators.length; i++) {
        const operatorNode = this.createValidatedOperatorNode(operators[i]);
        this.addToken(operatorNode);
        this.visit(rhsExpressions[i]);
      }
    }
  }

  additionExpression(ctx: BinaryExpressionCst): void {
    this.visit(ctx.lhs);

    const operators = [...(ctx.Plus || []), ...(ctx.Minus || [])] as IToken[];

    if (operators.length) {
      const rhsExpressions = ctx.rhs as CstNode[];

      for (let i = 0; i < operators.length; i++) {
        const operatorNode = this.createValidatedOperatorNode(operators[i]);
        this.addToken(operatorNode);
        this.visit(rhsExpressions[i]);
      }
    }
  }

  multiplicationExpression(ctx: BinaryExpressionCst): void {
    this.visit(ctx.lhs);

    const operators = [
      ...(ctx.Star || []),
      ...(ctx.Slash || []),
      ...(ctx.Percent || []),
    ] as IToken[];

    if (operators.length) {
      const rhsExpressions = ctx.rhs as CstNode[];

      for (let i = 0; i < operators.length; i++) {
        const operatorNode = this.createValidatedOperatorNode(operators[i]);
        this.addToken(operatorNode);
        this.visit(rhsExpressions[i]);
      }
    }
  }

  exponentiationExpression(ctx: BinaryExpressionCst): void {
    this.visit(ctx.lhs);

    const Caret = ctx.Caret;

    if (Caret) {
      const operators = Caret as IToken[];
      const rhsExpressions = ctx.rhs as CstNode[];

      for (let i = 0; i < operators.length; i++) {
        const operatorNode = this.createValidatedOperatorNode(operators[i]);
        this.addToken(operatorNode);
        this.visit(rhsExpressions[i]);
      }
    }
  }

  atomicExpression(ctx: AtomicExpressionCst): void {
    const expressionNode =
      ctx.unaryExpression ||
      ctx.numberLiteral ||
      ctx.stringLiteral ||
      ctx.attributeReference ||
      ctx.parenthesizedExpression ||
      ctx.functionCall ||
      ctx.uppercaseIdentifier;

    if (expressionNode) {
      this.visit(expressionNode);
    }
  }

  unaryExpression(ctx: { Minus: IToken[]; numberLiteral: CstNode[] }): void {
    const numberCst = ctx.numberLiteral[0] as TypedCstNode;
    const numberToken = (numberCst.children.NumberLiteral as IToken[])[0];

    const negativeNumberToken: IToken = {
      ...numberToken,
      image: `-${numberToken.image}`,
    };

    const node = this.createEditorNode(
      negativeNumberToken,
      EditorNodeName.number
    );
    this.addToken(node);
  }

  numberLiteral(ctx: NumberLiteralCst): void {
    const token = ctx.NumberLiteral[0] as IToken;
    const node = this.createEditorNode(token, EditorNodeName.number);
    this.addToken(node);
  }

  stringLiteral(ctx: StringLiteralCst): void {
    const token = ctx.StringLiteral[0] as IToken;
    const node = this.createEditorNode(token, EditorNodeName.regex);
    this.addToken(node);
  }

  attributeReference(ctx: AttributeReferenceCst): void {
    const token = ctx.AttributeName[0] as IToken;
    const cleanContent = stripColonPrefix(token.image);
    const attribute = this.findAttribute(token.image);
    const hasContext = !!ctx.attributeContext;
    const isCustomWithContext = cleanContent === SERVICE_CUSTOM && hasContext;
    const isValid = isCustomWithContext || this.isValidAttribute(token.image);

    const node = this.createEditorNode(
      token,
      isValid ? EditorNodeName.attribute : EditorNodeName.unrecognized,
      {
        attributeId: isCustomWithContext ? SERVICE_CUSTOM : attribute?.id,
      }
    );

    node.content = cleanContent;

    if (!isValid) {
      this.addValidationError(node, `Unknown attribute: ${cleanContent}`);
    }

    if (attribute) {
      node.content = attribute.name;
      const attributeContext =
        attribute.context && buildAttributeContext(attribute);

      if (attributeContext) {
        node.context = attributeContext;
      }
    }

    if (ctx.attributeContext) {
      const contextTokens = this.extractContextTokens(ctx.attributeContext[0]);
      const contextString = contextTokens.map((t) => t.image).join("");
      try {
        const parsedContext = JSON.parse(contextString);
        node.context = { ...node.context, ...parsedContext };
      } catch {
        // Invalid JSON in context, keep existing context
      }
    }

    this.addToken(node);
  }

  private getIdentifierNodeType(
    isValidFunction: boolean,
    isValidAttribute: boolean
  ): EditorNodeName {
    if (isValidFunction) {
      return EditorNodeName.function;
    }

    if (isValidAttribute) {
      return EditorNodeName.attribute;
    }

    return EditorNodeName.unrecognized;
  }

  uppercaseIdentifier(ctx: UppercaseIdentifierCst): void {
    const token = ctx.FunctionName[0] as IToken;
    const attribute = this.findAttribute(token.image);
    const isValidAttribute = this.isValidAttribute(token.image);
    const isValidFunction = this.isValidFunction(token.image);
    const nodeType = this.getIdentifierNodeType(
      isValidFunction,
      isValidAttribute
    );

    const node = this.createEditorNode(token, nodeType, {
      attributeId: isValidAttribute ? attribute?.id : undefined,
    });

    node.content = token.image;

    if (nodeType === EditorNodeName.unrecognized) {
      this.addValidationError(node, `Unknown identifier: ${token.image}`);
    }

    const isAttributeWithContext =
      attribute && nodeType === EditorNodeName.attribute;

    if (isAttributeWithContext) {
      node.content = attribute.name;
      const attributeContext =
        attribute.context && buildAttributeContext(attribute);

      if (attributeContext) {
        node.context = attributeContext;
      }
    }

    this.addToken(node);
  }

  private extractContextTokens(contextNode: CstNode): IToken[] {
    const tokens: IToken[] = [];

    const extractRecursive = (node: CstNode): void => {
      const children = (node as TypedCstNode).children;
      const allKeys = Object.keys(children || {});

      for (const key of allKeys) {
        const childArray = children[key];

        if (!Array.isArray(childArray)) {
          continue;
        }

        for (const item of childArray) {
          if ("image" in item) {
            tokens.push(item as IToken);
            continue;
          }

          if ("children" in item) {
            extractRecursive(item as CstNode);
          }
        }
      }
    };

    extractRecursive(contextNode);

    return tokens.sort((a, b) => a.startOffset - b.startOffset);
  }

  attributeContext(): void {
    // AttributeContext tokens are handled by extractContextTokens
    // This method exists to satisfy the visitor validator
    // The actual context parsing happens in attributeReference
  }

  nestedBraceGroup(): void {
    // NestedBraceGroup tokens are handled by extractContextTokens
    // This method exists to satisfy the visitor validator
  }

  nestedBracketGroup(): void {
    // NestedBracketGroup tokens are handled by extractContextTokens
    // This method exists to satisfy the visitor validator
  }

  argumentList(): void {
    // ArgumentList is handled directly in functionCall
    // This method exists to satisfy the visitor validator
  }

  parenthesizedExpression(ctx: ParenthesizedExpressionCst): void {
    const openingToken = ctx.LeftParenthesis?.[0] as IToken | undefined;
    const closingToken = ctx.RightParenthesis?.[0] as IToken | undefined;

    if (openingToken) {
      const openingNode = this.createEditorNode(
        openingToken,
        EditorNodeName.parenthesis
      );
      const openingIndex = this.getCurrentIndex();
      this.addToken(openingNode);

      this.context.bracketDepth++;
      if (ctx.expression) {
        this.visit(ctx.expression);
      }
      this.context.bracketDepth--;

      this.handleClosingBracket(
        closingToken,
        openingToken,
        openingNode,
        openingIndex
      );
    }
  }

  functionCall(ctx: FunctionCallCst): void {
    const FunctionName = ctx.FunctionName;
    const Operator =
      ctx.Plus ||
      ctx.Minus ||
      ctx.Star ||
      ctx.Slash ||
      ctx.Percent ||
      ctx.Caret ||
      ctx.LessThan ||
      ctx.GreaterThan ||
      ctx.Equals ||
      ctx.NotEquals ||
      ctx.LessEquals ||
      ctx.GreaterEquals ||
      ctx.LogicalAnd ||
      ctx.LogicalOr;
    const functionToken = (FunctionName || Operator)?.[0] as IToken;
    const functionName = functionToken.image.toUpperCase();
    const functionDef = this.context.functionsByNameMap[functionName];

    const openingToken = ctx.LeftParenthesis?.[0] as IToken | undefined;
    const closingToken = ctx.RightParenthesis?.[0] as IToken | undefined;

    const isRecoveryToken = (
      openingToken as IToken & { isInsertedInRecovery?: boolean }
    )?.isInsertedInRecovery;
    const hasRealParenthesis = openingToken && !isRecoveryToken;
    const isOperatorToken = Boolean(Operator);

    if (isOperatorToken && !hasRealParenthesis) {
      const operatorNode = this.createEditorNode(
        functionToken,
        EditorNodeName.operator
      );
      this.addToken(operatorNode);
      return;
    }

    const isValidFunction =
      this.isValidFunction(functionToken.image) || isOperatorToken;

    if (!isValidFunction && !hasRealParenthesis) {
      const attribute = this.findAttribute(functionToken.image);

      if (attribute) {
        this.addAttributeNodeFromFunctionCall(functionToken, attribute);
        return;
      }
    }

    const functionNode = this.createEditorNode(
      functionToken,
      EditorNodeName.function
    );

    if (!isValidFunction) {
      functionNode.type = EditorNodeName.unrecognized;
      this.addValidationError(
        functionNode,
        `Unknown function or operator: ${functionToken.image}`
      );
    }

    const functionStartIndex = this.getCurrentIndex();
    this.addToken(functionNode);

    if (!hasRealParenthesis) {
      return;
    }

    const openingNode = this.createEditorNode(
      openingToken,
      EditorNodeName.parenthesis
    );
    const openingIndex = this.getCurrentIndex();
    this.addToken(openingNode);

    const argumentRanges: FunctionCallMetadata["argumentRanges"] = [];

    this.context.bracketDepth++;

    if (ctx.argumentList) {
      const argumentListNode = ctx
        .argumentList[0] as unknown as ArgumentListCst;
      const args = argumentListNode.children.argument as CstNode[];
      const commas = argumentListNode.children.Comma;

      for (let i = 0; i < args.length; i++) {
        const argStartIndex = this.getCurrentIndex();
        this.visit(args[i]);
        const argEndIndex = this.getCurrentIndex() - 1;

        argumentRanges.push({
          startIndex: argStartIndex,
          endIndex: argEndIndex,
          argumentIndex: i,
          parameterDef: this.getParameterForIndex(i, functionDef),
        });

        if (i < args.length - 1 && commas) {
          const commaToken = commas[i] as IToken;
          const commaNode = this.createEditorNode(
            commaToken,
            EditorNodeName.separator
          );
          this.addToken(commaNode);
        }
      }
    }

    this.context.bracketDepth--;

    const closingIndex = this.handleClosingBracket(
      closingToken,
      openingToken,
      openingNode,
      openingIndex
    );

    this.metadata.functionCalls.push({
      functionName,
      definition: functionDef,
      startIndex: functionStartIndex,
      endIndex: closingIndex,
      argumentRanges,
    });
  }

  private getParameterForIndex(
    index: number,
    functionDef?: FormulaOption
  ): FormulaOption["parameters"][number] | undefined {
    if (!functionDef?.parameters) {
      return undefined;
    }

    const parameters = functionDef.parameters;
    const parametersLength = parameters.length;

    if (index <= parametersLength - 1) {
      return parameters[index];
    }

    const isLastParameterVariadic = parameters[parametersLength - 1]?.variadic;
    if (index > parametersLength - 1 && isLastParameterVariadic) {
      return parameters[parametersLength - 1];
    }

    return undefined;
  }

  public parse(cst: CstNode): EditorNode[] {
    this.tokens = [];
    this.context.sequentialIndex = 0;
    this.context.bracketDepth = 0;
    this.metadata = {
      bracketPairs: new Map(),
      functionCalls: [],
      totalTokens: 0,
    };

    this.visit(cst);

    this.metadata.totalTokens = this.tokens.length;
    return this.tokens;
  }
}

export { FormulaVisitor };
